import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ExternalLink, FolderOpen, Pencil, Trash2, Search, CheckCircle2, AlertCircle, ShieldCheck, Clock, XCircle, Filter } from 'lucide-react';
import { API } from '@/App';
import { formatDate } from '@/utils/dateFormat';
// iter-364 — Featured Brand Partner placement on Vendor Catalogues (marketplace).
import SponsorBadge from '@/components/SponsorBadge';

const STATUS_CONFIG = {
  not_set: { label: 'Not Set', color: 'bg-stone-100 text-stone-600', icon: XCircle, dotColor: 'bg-stone-400' },
  link_added: { label: 'Link Added', color: 'bg-amber-100 text-amber-700', icon: Clock, dotColor: 'bg-amber-400' },
  verified: { label: 'Verified', color: 'bg-blue-100 text-blue-700', icon: ShieldCheck, dotColor: 'bg-blue-500' },
  in_use: { label: 'In Use', color: 'bg-green-100 text-green-700', icon: CheckCircle2, dotColor: 'bg-green-500' },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.not_set;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} text-[10px] px-1.5 py-0.5 gap-1 font-medium`} data-testid={`status-badge-${status}`}>
      <Icon size={10} /> {config.label}
    </Badge>
  );
}

export default function VendorCatalogues() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['owner', 'admin', 'staff', 'project_manager'].includes(user.role);

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [linkInput, setLinkInput] = useState('');
  const [folderPathInput, setFolderPathInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [linkValidation, setLinkValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [markingActivity, setMarkingActivity] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [filesCount, setFilesCount] = useState('');
  const [activityNotes, setActivityNotes] = useState('');

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vendor-catalogues`);
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to load catalogue links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  // Link validation
  const validateLink = useCallback(async (url) => {
    if (!url || url.length < 10) { setLinkValidation(null); return; }
    setValidating(true);
    try {
      const res = await axios.post(`${API}/vendor-catalogues/validate-link`, { onedrive_link: url });
      setLinkValidation(res.data);
    } catch {
      setLinkValidation(null);
    } finally {
      setValidating(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (linkInput) validateLink(linkInput); }, 500);
    return () => clearTimeout(timer);
  }, [linkInput, validateLink]);

  const handleSaveLink = async (e) => {
    e.preventDefault();
    if (!editVendor || !linkInput.trim()) { toast.error('Enter a valid OneDrive link'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/vendor-catalogues/${editVendor.vendor_id}/link`, {
        onedrive_link: linkInput.trim(),
        folder_path: folderPathInput.trim() || null,
      });
      const validation = res.data.link_validation;
      if (validation && !validation.valid) {
        toast.warning(`Link saved, but: ${validation.reason}`);
      } else {
        toast.success(`Catalogue link saved for ${editVendor.vendor_name}`);
      }
      setEditDialogOpen(false);
      resetEditState();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async (vendorId) => {
    try {
      await axios.put(`${API}/vendor-catalogues/${vendorId}/verify`);
      toast.success('Link marked as verified');
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify');
    }
  };

  const handleRemoveLink = async (vendorId, vendorName) => {
    if (!window.confirm(`Remove catalogue link for ${vendorName}?`)) return;
    try {
      await axios.delete(`${API}/vendor-catalogues/${vendorId}/link`);
      toast.success('Catalogue link removed');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to remove link');
    }
  };

  const handleMarkActivity = async (e) => {
    e.preventDefault();
    setMarkingActivity(true);
    try {
      await axios.post(`${API}/vendor-catalogues/mark-activity`, {
        files_count: filesCount ? parseInt(filesCount) : null,
        notes: activityNotes || null,
      });
      toast.success('Upload confirmed! Admin has been notified.');
      setActivityDialogOpen(false);
      setFilesCount('');
      setActivityNotes('');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to record activity');
    } finally {
      setMarkingActivity(false);
    }
  };

  const openEditDialog = (vendor) => {
    setEditVendor(vendor);
    setLinkInput(vendor.onedrive_link || '');
    setFolderPathInput(vendor.folder_path || `Vendor_Catalogues/${vendor.vendor_name.replace(/\s+/g, '_')}_${vendor.vendor_id.slice(0, 8)}/`);
    setLinkValidation(null);
    setEditDialogOpen(true);
  };

  const resetEditState = () => {
    setEditVendor(null);
    setLinkInput('');
    setFolderPathInput('');
    setLinkValidation(null);
  };

  const openOneDrive = (link) => {
    if (link) window.open(link, '_blank', 'noopener,noreferrer');
    else toast.error('No catalogue link configured yet. Contact admin.');
  };

  // Filtering
  const filteredVendors = vendors.filter(v => {
    const matchesSearch = !searchQuery ||
      v.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.vendor_email?.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (activeFilter === 'all') return true;
    return v.status === activeFilter;
  });

  const counts = {
    all: vendors.length,
    not_set: vendors.filter(v => v.status === 'not_set').length,
    link_added: vendors.filter(v => v.status === 'link_added').length,
    verified: vendors.filter(v => v.status === 'verified').length,
    in_use: vendors.filter(v => v.status === 'in_use').length,
  };

  // ========== VENDOR VIEW ==========
  if (!isAdmin) {
    const myData = vendors[0];
    return (
      <Layout>
        <div className="p-6 max-w-3xl mx-auto" data-testid="vendor-catalogues-page">
          <h1 className="text-2xl font-bold text-stone-900 mb-1">My Catalogues</h1>
          <p className="text-sm text-stone-500 mb-8">Manage your product catalogues and documents via OneDrive</p>

          {loading ? (
            <div className="text-center py-12 text-stone-400">Loading...</div>
          ) : myData?.onedrive_link ? (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-stone-200 p-8 text-center space-y-5">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl flex items-center justify-center mx-auto">
                  <FolderOpen size={36} className="text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-stone-900">Your Catalogue Folder</h2>
                  <p className="text-sm text-stone-500 mt-2 max-w-md mx-auto">
                    Open your dedicated OneDrive folder to upload PDFs, images, spreadsheets, and catalogues.
                  </p>
                </div>
                {myData.folder_path && (
                  <p className="text-xs text-stone-400 font-mono bg-stone-50 inline-block px-3 py-1.5 rounded-lg border border-stone-100">{myData.folder_path}</p>
                )}
                <div>
                  <Button
                    onClick={() => openOneDrive(myData.onedrive_link)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-3 text-base rounded-xl"
                    data-testid="open-onedrive-btn"
                  >
                    <FolderOpen size={20} className="mr-2" /> Open Catalogue Folder
                  </Button>
                </div>
              </div>

              {/* Mark Activity */}
              <div className="bg-white rounded-xl border border-stone-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-stone-800">Uploaded new files?</h3>
                    <p className="text-xs text-stone-400 mt-0.5">Let the admin know you've added new catalogues</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setActivityDialogOpen(true)}
                    className="text-sm"
                    data-testid="mark-uploaded-btn"
                  >
                    <CheckCircle2 size={14} className="mr-1.5" /> Confirm Upload
                  </Button>
                </div>
                {myData.last_activity && (
                  <p className="text-xs text-green-600 mt-3 flex items-center gap-1">
                    <CheckCircle2 size={12} />
                    Last activity: {formatDate(new Date(myData.last_activity), { shortMonth: true, includeTime: true })}
                    {myData.files_count > 0 && ` · ${myData.files_count} files`}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-stone-200 p-8 text-center space-y-3">
              <AlertCircle size={48} className="mx-auto text-amber-400" />
              <h2 className="text-lg font-semibold text-stone-900">Folder Not Set Up Yet</h2>
              <p className="text-sm text-stone-500">Your catalogue folder hasn't been configured. Please contact the admin team.</p>
            </div>
          )}

          {/* Activity Dialog */}
          <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Confirm Upload</DialogTitle></DialogHeader>
              <form onSubmit={handleMarkActivity}>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>How many files did you upload?</Label>
                    <Input type="number" min="1" value={filesCount} onChange={(e) => setFilesCount(e.target.value)} placeholder="e.g. 5" className="mt-1" data-testid="files-count-input" />
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Input value={activityNotes} onChange={(e) => setActivityNotes(e.target.value)} placeholder="e.g. Updated plywood catalogue 2026" className="mt-1" data-testid="activity-notes-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setActivityDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={markingActivity} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-activity-btn">
                    {markingActivity ? 'Sending...' : 'Confirm & Notify Admin'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </Layout>
    );
  }

  // ========== ADMIN VIEW ==========
  const FILTER_TABS = [
    { key: 'all', label: 'All', color: 'stone' },
    { key: 'not_set', label: 'Pending Setup', color: 'red' },
    { key: 'link_added', label: 'Link Added', color: 'amber' },
    { key: 'verified', label: 'Verified', color: 'blue' },
    { key: 'in_use', label: 'Active', color: 'green' },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="vendor-catalogues-page">
        {/* iter-364 — Sponsored placement (zone=vendor_marketplace). */}
        <div className="mb-4"><SponsorBadge zone="vendor_marketplace" limit={1} variant="premium-minimal" /></div>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-stone-900">Vendor Catalogues</h1>
          <p className="text-sm text-stone-500 mt-1">Manage OneDrive folder links. No files stored in app.</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-5 gap-3 mb-5">
          {FILTER_TABS.map(tab => {
            const isActive = activeFilter === tab.key;
            const colorMap = {
              stone: 'border-stone-200 text-stone-600',
              red: 'border-red-200 text-red-600',
              amber: 'border-amber-200 text-amber-600',
              blue: 'border-blue-200 text-blue-600',
              green: 'border-green-200 text-green-600',
            };
            return (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={`bg-white rounded-xl border p-3 text-left transition-all cursor-pointer ${
                  isActive ? `ring-2 ring-stone-900 ${colorMap[tab.color]}` : `${colorMap[tab.color]} hover:shadow-sm`
                }`}
                data-testid={`filter-${tab.key}`}
              >
                <p className="text-[10px] uppercase tracking-wider font-medium">{tab.label}</p>
                <p className="text-xl font-bold mt-0.5">{counts[tab.key]}</p>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="mb-4 relative">
          <Search size={16} className="absolute left-3 top-3 text-stone-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search vendors..." className="pl-10" data-testid="search-vendors" />
        </div>

        {/* Onboarding Checklist */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">Vendor Onboarding Checklist</h3>
          <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
            <li>Create folder in OneDrive: <span className="font-mono bg-blue-100 px-1 rounded">/Vendor_Catalogues/VendorName_VendorID/</span></li>
            <li>Right-click folder &rarr; Share &rarr; "Anyone with the link can edit"</li>
            <li>Click "Set Link" below and paste the share URL</li>
            <li>Click "Verify" after confirming the link works</li>
            <li>Vendor sees "Open Catalogue Folder" on their dashboard</li>
          </ol>
        </div>

        {/* Vendor List */}
        {loading ? (
          <div className="text-center py-12 text-stone-400">Loading vendors...</div>
        ) : filteredVendors.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
            <FolderOpen size={48} className="mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500">{activeFilter !== 'all' ? 'No vendors in this filter' : 'No vendors found'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredVendors.map((v) => (
              <div key={v.vendor_id} className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-4 hover:shadow-sm transition-shadow" data-testid={`vendor-row-${v.vendor_id}`}>
                {/* Status dot */}
                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${STATUS_CONFIG[v.status]?.dotColor || 'bg-stone-300'}`} />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-stone-900">{v.vendor_name}</p>
                    <StatusBadge status={v.status} />
                  </div>
                  <p className="text-xs text-stone-400">{v.vendor_email}</p>
                  {v.folder_path && <p className="text-xs text-stone-400 font-mono mt-0.5 truncate">{v.folder_path}</p>}
                  <div className="flex items-center gap-3 mt-1">
                    {v.updated_at && (
                      <p className="text-[10px] text-stone-300">
                        Link set {formatDate(new Date(v.updated_at), { shortMonth: true })}
                        {v.updated_by && ` by ${v.updated_by}`}
                      </p>
                    )}
                    {v.last_activity && (
                      <p className="text-[10px] text-green-500">
                        Last upload: {formatDate(new Date(v.last_activity), { shortMonth: true })}
                      </p>
                    )}
                    {v.files_count > 0 && (
                      <p className="text-[10px] text-stone-400">{v.files_count} files</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {v.onedrive_link && (
                    <Button variant="outline" size="sm" onClick={() => openOneDrive(v.onedrive_link)} className="text-xs" data-testid={`view-catalogue-${v.vendor_id}`}>
                      <ExternalLink size={14} className="mr-1" /> View
                    </Button>
                  )}
                  {v.status === 'link_added' && (
                    <Button variant="outline" size="sm" onClick={() => handleVerify(v.vendor_id)} className="text-xs text-blue-600 border-blue-200 hover:bg-blue-50" data-testid={`verify-link-${v.vendor_id}`}>
                      <ShieldCheck size={14} className="mr-1" /> Verify
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(v)} className="text-xs" data-testid={`edit-link-${v.vendor_id}`}>
                    <Pencil size={14} className="mr-1" /> {v.onedrive_link ? 'Edit' : 'Set Link'}
                  </Button>
                  {v.onedrive_link && (
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveLink(v.vendor_id, v.vendor_name)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`remove-link-${v.vendor_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Link Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) resetEditState(); }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editVendor?.onedrive_link ? 'Update' : 'Set'} Catalogue Link — {editVendor?.vendor_name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveLink}>
              <div className="space-y-4 py-4">
                <div>
                  <Label>OneDrive Share Link *</Label>
                  <Input
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    placeholder="https://1drv.ms/... or https://company.sharepoint.com/..."
                    className="mt-1"
                    required
                    data-testid="onedrive-link-input"
                  />
                  {/* Validation feedback */}
                  {validating && <p className="text-xs text-stone-400 mt-1">Validating...</p>}
                  {!validating && linkValidation && (
                    <div className={`flex items-center gap-1.5 mt-1.5 text-xs ${linkValidation.valid ? 'text-green-600' : 'text-red-500'}`} data-testid="link-validation-result">
                      {linkValidation.valid ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                      {linkValidation.valid ? 'Valid OneDrive/SharePoint link' : linkValidation.reason}
                    </div>
                  )}
                  <p className="text-xs text-stone-400 mt-1">Paste the "Anyone with the link can edit" share URL</p>
                </div>
                <div>
                  <Label>Folder Path</Label>
                  <Input
                    value={folderPathInput}
                    onChange={(e) => setFolderPathInput(e.target.value)}
                    placeholder={`Vendor_Catalogues/${editVendor?.vendor_name || 'Name'}_${(editVendor?.vendor_id || '').slice(0, 8)}/`}
                    className="mt-1 font-mono text-xs"
                    data-testid="folder-path-input"
                  />
                  <p className="text-xs text-stone-400 mt-1">
                    Recommended: <span className="font-mono bg-stone-50 px-1 rounded">Vendor_Catalogues/{'{Vendor_Name}'}_{'{ID}'}/</span>
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setEditDialogOpen(false); resetEditState(); }}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="save-link-btn">
                  {saving ? 'Saving...' : 'Save Link'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
